import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, ErrorAlert, Field, Modal, PageHeader, Pagination, Spinner } from '../components/ui';
import { PROMPT_CATEGORIES } from '@kontentowo/config';
import type { Prompt } from '@kontentowo/shared';

interface PromptForm {
  name: string;
  slug: string;
  category: string;
  version: number;
  systemPrompt: string;
  userTemplate: string;
  outputSchema: string;
  enabled: boolean;
}

const EMPTY: PromptForm = {
  name: '',
  slug: '',
  category: 'SOCIAL_POST',
  version: 1,
  systemPrompt: '',
  userTemplate: '',
  outputSchema: '',
  enabled: true,
};

function slugify(value: string): string {
  return value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export function PromptLibraryPage() {
  const { data, error, loading, reload } = useAsync(() => api.prompts(), []);
  const [editing, setEditing] = useState<Prompt | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<PromptForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setSaveError(null);
    setOpen(true);
  };

  const openEdit = (prompt: Prompt) => {
    setEditing(prompt);
    setForm({
      name: prompt.name,
      slug: prompt.slug,
      category: prompt.category,
      version: prompt.version,
      systemPrompt: prompt.systemPrompt,
      userTemplate: prompt.userTemplate,
      outputSchema: prompt.outputSchema ? JSON.stringify(prompt.outputSchema, null, 2) : '',
      enabled: prompt.enabled,
    });
    setSaveError(null);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      let outputSchema: Record<string, unknown> | null = null;
      if (form.outputSchema.trim()) outputSchema = JSON.parse(form.outputSchema) as Record<string, unknown>;
      const body = {
        name: form.name,
        slug: form.slug || slugify(form.name),
        category: form.category,
        version: Number(form.version) || 1,
        systemPrompt: form.systemPrompt,
        userTemplate: form.userTemplate,
        outputSchema,
        enabled: form.enabled,
      };
      if (editing) await api.promptUpdate(editing.id, body);
      else await api.promptCreate(body);
      setOpen(false);
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Prompt Library"
        subtitle="Prompty są wersjonowane w bazie — nie są hardcodowane w serwisach."
        actions={<Button variant="primary" onClick={openCreate}>+ Nowy prompt</Button>}
      />
      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}
      {data && (
        <Card flush>
          <table className="table">
            <thead>
              <tr>
                <th>Nazwa</th>
                <th>Slug</th>
                <th>Kategoria</th>
                <th>Wersja</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {data.slice((page - 1) * pageSize, page * pageSize).map((p) => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 550 }}>{p.name}</td>
                  <td className="mono">{p.slug}</td>
                  <td>
                    <Badge tone="info">{p.category}</Badge>
                  </td>
                  <td>v{p.version}</td>
                  <td>
                    <Badge tone={p.enabled ? 'success' : 'neutral'}>{p.enabled ? 'enabled' : 'off'}</Badge>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Button size="sm" onClick={() => openEdit(p)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={data.length} onChange={setPage} />
        </Card>
      )}

      <Modal
        title={editing ? `Edytuj prompt: ${editing.name}` : 'Nowy prompt'}
        open={open}
        onClose={() => setOpen(false)}
        wide
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button variant="primary" onClick={save} disabled={saving || !form.name || !form.systemPrompt || !form.userTemplate}>
              {saving ? 'Zapisywanie…' : 'Zapisz'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={saveError} />
        <div className="form-row">
          <Field label="Nazwa">
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} />
          </Field>
          <Field label="Slug">
            <input className="input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          </Field>
        </div>
        <div className="form-row">
          <Field label="Kategoria">
            <select className="select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {PROMPT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Wersja">
            <input className="input" type="number" value={form.version} onChange={(e) => setForm({ ...form, version: Number(e.target.value) })} />
          </Field>
        </div>
        <Field label="System prompt">
          <textarea className="textarea" style={{ minHeight: 110 }} value={form.systemPrompt} onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })} />
        </Field>
        <Field label="User template" hint="Zmienne: {brand.name}, {topic}, {goal}, {language}, {platforms}, {context}">
          <textarea className="textarea" style={{ minHeight: 110 }} value={form.userTemplate} onChange={(e) => setForm({ ...form, userTemplate: e.target.value })} />
        </Field>
        <Field label="Output schema (JSON, opcjonalnie)">
          <textarea className="textarea mono" style={{ minHeight: 90 }} value={form.outputSchema} onChange={(e) => setForm({ ...form, outputSchema: e.target.value })} />
        </Field>
        <label className="inline" style={{ gap: 8 }}>
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          Enabled
        </label>
      </Modal>
    </>
  );
}
